import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import {
    Chart,
    ChartDefinition,
    ChartFilter,
    ChartGroupByMode,
    ChartQueryParam,
    ChartQueryParams,
    PieData,
    isPieData,
    normalizeChart,
    pieDataToChart
} from '@frontend/chart/types';

const filtersToQueryParams = (filters: ChartFilter[]): ApiQueryParams<ChartQueryParams> =>
    Object.fromEntries(
        filters.map((filter) => [filter.key, Array.isArray(filter.value) ? filter.value.map(String) : String(filter.value)])
    ) as ApiQueryParams<ChartQueryParams>;

export class ChartClient extends APIClient {
    public static async fetchChartData(accountId: string, definition: ChartDefinition, queryParams?: ApiQueryParams<ChartQueryParams>): Promise<Chart> {
        return await this.fetchChartDataFromEndpoint(definition.endpoint.replace('{accountId}', accountId), definition, {
            [DefaultQueryParams.ACCOUNT_ID]: accountId,
            ...queryParams
        });
    }

    public static async fetchChartDataForAllAccounts(definition: ChartDefinition, queryParams?: ApiQueryParams<ChartQueryParams>): Promise<Chart> {
        return await this.fetchChartDataFromEndpoint(definition.endpoint, definition, queryParams);
    }

    private static async fetchChartDataFromEndpoint(
        endpoint: string,
        definition: ChartDefinition,
        queryParams?: ApiQueryParams<ChartQueryParams>
    ): Promise<Chart> {
        const groupingParams: ApiQueryParams<ChartQueryParams> = definition.groupBy
            ? {
                  [ChartQueryParam.GROUP_BY]: definition.groupBy,
                  [ChartQueryParam.GROUP_BY_MODE]: definition.groupByMode ?? ChartGroupByMode.VALUE
              }
            : {};

        const response = await this.fetchWithQueryParams(endpoint, undefined, true, undefined, {
            ...filtersToQueryParams(definition.filters),
            ...groupingParams,
            ...queryParams
        });
        const data = await this.handleResponse<Chart | PieData>(response);
        // Some equipment endpoints still return { series: number[], labels: string[] }.
        const chart = isPieData(data) ? pieDataToChart(data) : data;
        return normalizeChart(chart);
    }
}
